import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";
import toast from "react-hot-toast";
import {
  getCompanyServiceCommencementDate,
  getEmployeeDoj,
  getFirstEligibleMonthInYear,
  getLeaveBalanceYearOptions,
  resolveLeaveBalanceStartDate,
} from "../../../../../lib/leaveBalanceDates";
import { roundLeaveDaysForDisplay } from "../../../../../lib/leaveBalanceDisplay";
import { filterLeaveTypesByGender } from "../../../../../lib/leaveGenderFilter";
import {
  PHONE_SHEET_BACKDROP_CLS,
  PHONE_SHEET_PANEL_CLS,
  usePhoneFormBack,
} from "../RequestHistoryOverlay";

const getEmployeeDbId = (employee) => {
  if (!employee) return null;
  const n = Number(employee.id ?? employee.employeeId);
  return Number.isFinite(n) ? n : null;
};

const getEmployeeCode = (employee) => {
  if (!employee) return "";
  const code =
    employee.empCode || employee.employeeCode || employee.code || "";
  const asStr = String(code || "").trim();
  if (asStr) return asStr;
  const alt = employee.employeeId;
  if (alt != null && String(alt) !== String(employee.id)) {
    return String(alt).trim();
  }
  return "";
};

const getEmployeeDisplayName = (employee) => {
  if (!employee) return "";
  const fromUser = [employee.user?.name, employee.user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromUser) return fromUser;
  const direct =
    employee.employeeName ||
    employee.name ||
    employee.fullName ||
    [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
  return String(direct || "").trim();
};

const formatEmployeeCodeAndName = (employee) => {
  const code = getEmployeeCode(employee);
  const name = getEmployeeDisplayName(employee);
  if (code && name) return `${code} - ${name}`;
  if (code) return code;
  if (name) return name;
  return "Employee";
};

const getEmployeeGender = (employee) =>
  employee?.user?.gender || employee?.gender || "";

const mergeCompanyLeaveTypesForEmployee = (
  apiLeaveTypes,
  companyLeaveTypes,
  employee
) => {
  const companyById = new Map(
    (companyLeaveTypes || [])
      .filter((leaveType) => leaveType?.id)
      .map((leaveType) => [Number(leaveType.id), leaveType])
  );

  const entitled = (apiLeaveTypes || [])
    .map((leaveType) => {
      if (!leaveType?.id) return null;
      const companyLt = companyById.get(Number(leaveType.id));
      const isActive =
        leaveType.isActive !== undefined
          ? leaveType.isActive
          : companyLt?.isActive !== undefined
            ? companyLt.isActive
            : true;
      if (!isActive) return null;
      return {
        id: leaveType.id,
        name: leaveType.name || leaveType.leaveTypeName || companyLt?.name,
        code: leaveType.code || companyLt?.code,
        genderApplicability:
          leaveType.genderApplicability ?? companyLt?.genderApplicability,
      };
    })
    .filter(Boolean);

  return filterLeaveTypesByGender(entitled, getEmployeeGender(employee));
};

/**
 * Leave Balance popup (Admin Attendance Actions + ESS Attendance calendar).
 * Shows monthly opening/credited/availed/adjusted/encashed/closing for one employee.
 */
export default function LeaveBalancePopup({
  darkMode = false,
  isOpen,
  onClose,
  employee,
  leaveTypes = [],
  attendanceStructureService,
  refreshKey = 0,
}) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState("");
  const [balanceData, setBalanceData] = useState([]);
  const [balancePolicy, setBalancePolicy] = useState(null);
  const [balanceEntitlement, setBalanceEntitlement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eligibleLeaveTypes, setEligibleLeaveTypes] = useState([]);
  const [employeeLeaveGroups, setEmployeeLeaveGroups] = useState([]);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [balanceYearOptions, setBalanceYearOptions] = useState([]);
  const [balanceStartDate, setBalanceStartDate] = useState(null);
  const [balanceRounding, setBalanceRounding] = useState("full_day");
  const [balanceInfoOpen, setBalanceInfoOpen] = useState(false);
  const [creditHint, setCreditHint] = useState(null);

  const companyServiceDate = useMemo(() => getCompanyServiceCommencementDate(), []);
  const employeeDoj = useMemo(() => getEmployeeDoj(employee), [employee]);
  const employeeDbId = getEmployeeDbId(employee);

  const monthLabels = useMemo(() => {
    const labels = [];
    const firstMonth = getFirstEligibleMonthInYear(selectedYear, balanceStartDate);
    if (firstMonth == null) return labels;
    const lastMonth =
      selectedYear < currentYear ? 12 : Math.min(currentMonth, 12);
    for (let m = firstMonth; m <= lastMonth; m += 1) {
      labels.push({
        month: m,
        label: new Date(selectedYear, m - 1, 1).toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });
    }
    return labels;
  }, [selectedYear, currentYear, currentMonth, balanceStartDate]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedYear(currentYear);
      setSelectedLeaveTypeId("");
      setBalanceData([]);
      setBalancePolicy(null);
      setBalanceEntitlement(null);
      setEligibleLeaveTypes([]);
      setEmployeeLeaveGroups([]);
      setBalanceYearOptions([]);
      setBalanceStartDate(null);
      setBalanceRounding("full_day");
      setBalanceInfoOpen(false);
      setCreditHint(null);
    }
  }, [isOpen, currentYear]);

  useEffect(() => {
    if (!isOpen || !employeeDbId || !attendanceStructureService?.getEmployeeLeaveTypes) {
      return;
    }
    let cancelled = false;
    setEntitlementLoading(true);
    attendanceStructureService
      .getEmployeeLeaveTypes(employeeDbId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setEligibleLeaveTypes(list);
        setEmployeeLeaveGroups(res?.leaveGroups ?? []);
        const merged = mergeCompanyLeaveTypesForEmployee(list, leaveTypes, employee);
        if (merged.length === 1) {
          setSelectedLeaveTypeId(String(merged[0].id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEligibleLeaveTypes([]);
          setEmployeeLeaveGroups([]);
        }
      })
      .finally(() => {
        if (!cancelled) setEntitlementLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeDbId, employee, attendanceStructureService, refreshKey, leaveTypes]);

  const leaveTypeOptions = useMemo(
    () =>
      mergeCompanyLeaveTypesForEmployee(eligibleLeaveTypes, leaveTypes, employee),
    [eligibleLeaveTypes, leaveTypes, employee]
  );

  useEffect(() => {
    if (!isOpen || !employeeDbId) return;
    const leaveTypeId = selectedLeaveTypeId ? Number(selectedLeaveTypeId) : null;
    if (!leaveTypeId) {
      setBalanceData([]);
      setBalancePolicy(null);
      setBalanceEntitlement(null);
      setBalanceYearOptions([]);
      setBalanceStartDate(null);
      setCreditHint(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    attendanceStructureService
      .getLeaveBalance(employeeDbId, selectedYear, leaveTypeId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.months ?? data?.list ?? [];
        setBalanceData(list);
        setBalancePolicy(data?.policy ?? null);
        setBalanceEntitlement(data?.entitlement ?? null);
        setCreditHint(data?.creditHint || null);
        setBalanceRounding(data?.policy?.balanceRounding ?? "full_day");
        const effectiveFrom = data?.policy?.effectiveFrom ?? null;
        const startDate = data?.balanceStartDate
          ? new Date(`${data.balanceStartDate}T12:00:00`)
          : resolveLeaveBalanceStartDate({
              serviceCommencementDate: companyServiceDate,
              employeeDoj,
              effectiveFromDate: effectiveFrom,
            });
        setBalanceStartDate(startDate);
        const yearOptions =
          Array.isArray(data?.yearOptions) && data.yearOptions.length
            ? data.yearOptions
            : getLeaveBalanceYearOptions({ startDate });
        setBalanceYearOptions(yearOptions);
        if (yearOptions.length && !yearOptions.includes(selectedYear)) {
          setSelectedYear(yearOptions[0]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBalanceData([]);
          toast.error(
            err?.response?.data?.message || err?.message || "Failed to load leave balance"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    employeeDbId,
    selectedYear,
    selectedLeaveTypeId,
    attendanceStructureService,
    refreshKey,
    companyServiceDate,
    employeeDoj,
  ]);

  const yearOptions = balanceYearOptions.length
    ? balanceYearOptions
    : getLeaveBalanceYearOptions({ startDate: balanceStartDate });

  const getRow = (monthNum) => {
    const row = balanceData.find((r) => Number(r.month) === monthNum);
    if (row?.ineligible) return { ineligible: true };
    return {
      openingBalance: row?.openingBalance ?? 0,
      credited: row?.credited ?? 0,
      availed: row?.availed ?? 0,
      adjustedDebit: row?.adjustedDebit ?? 0,
      adjustedCredit: row?.adjustedCredit ?? 0,
      encashed: row?.encashed ?? 0,
      closingBalance: row?.closingBalance ?? 0,
    };
  };

  const formatBalanceCell = (value, ineligible) => {
    if (ineligible || value == null) return "—";
    return roundLeaveDaysForDisplay(value, balanceRounding);
  };

  const balanceInfoLines = useMemo(() => {
    const lines = [];
    if (balanceStartDate) {
      const fromLabel = balanceStartDate.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      lines.push(
        `Balance applies from ${fromLabel} (latest of service commencement, leave type effective date, and employee DOJ).`
      );
    }
    if (balancePolicy) {
      let policy = `Policy: ${balancePolicy.creditFrequency} · ${balancePolicy.creditMethod}`;
      if (balancePolicy.effectiveFrom) {
        policy += ` · effective from ${balancePolicy.effectiveFrom}`;
      }
      if (balancePolicy.creditOn) {
        policy += ` · credits on ${balancePolicy.creditOn}`;
      }
      if (
        (balancePolicy.creditMethod === "Based on Attendance" ||
          balancePolicy.creditMethod === "Based on Working Days") &&
        balancePolicy.attendanceDaysRequired
      ) {
        const unit =
          balancePolicy.creditMethod === "Based on Working Days"
            ? "working day(s) (PR/OD/CO)"
            : "paid day(s)";
        policy += ` · ${balancePolicy.leavesCreditedPerAttendanceDays} day(s) per ${balancePolicy.attendanceDaysRequired} ${unit}`;
      }
      lines.push(policy);
    }
    if (employeeLeaveGroups.length > 0) {
      lines.push(
        `Leave groups: ${employeeLeaveGroups.map((g) => g.name).join(", ")}`
      );
    }
    return lines;
  }, [employeeLeaveGroups, balanceStartDate, balancePolicy]);
  usePhoneFormBack(isOpen, onClose);

  if (!isOpen || typeof document === "undefined") return null;

  const pinnedMonthCls = `max-md:sticky max-md:left-0 max-md:z-[1] ${
    darkMode ? "max-md:bg-[hsl(var(--ds-muted))]" : "max-md:bg-[hsl(var(--ds-surface))]"
  }`;

  return createPortal(
    <div className={`fixed inset-0 z-[11050] flex items-center justify-center bg-black/50 ${PHONE_SHEET_BACKDROP_CLS}`}>
      <div
        className={`flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg shadow-xl ${PHONE_SHEET_PANEL_CLS} ${
          darkMode
            ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
            : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Leave Balance"
      >
        <div
          className={`flex items-center justify-between border-b px-4 py-3 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <h2 className="text-lg font-semibold">Leave Balance</h2>
          <div className="flex items-center gap-1">
            {balanceInfoLines.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBalanceInfoOpen((open) => !open);
                  }}
                  onBlur={() => setBalanceInfoOpen(false)}
                  className={`rounded p-1 text-[hsl(var(--ds-muted-fg))] ${
                    darkMode
                      ? "hover:bg-[hsl(var(--ds-muted))]"
                      : "hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                  aria-label="Leave balance details"
                  aria-expanded={balanceInfoOpen}
                >
                  <Info size={18} strokeWidth={1.75} />
                </button>
                {balanceInfoOpen ? (
                  <div
                    className="absolute right-0 top-full z-20 mt-1 w-72 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 text-left text-xs leading-snug text-[hsl(var(--ds-muted-fg))] shadow-lg sm:w-80"
                    role="tooltip"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <ul className="space-y-2">
                      {balanceInfoLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`rounded p-1 ${
                darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"
              }`}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        {employee && (
          <p
            className={`px-4 py-2 text-sm ${
              darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            {formatEmployeeCodeAndName(employee)}
          </p>
        )}
        {!entitlementLoading && leaveTypeOptions.length === 0 && (
          <p
            className={`mx-4 mb-2 rounded px-3 py-2 text-sm ${
              darkMode ? "bg-amber-900/30 text-amber-200" : "bg-amber-50 text-amber-800"
            }`}
          >
            No leave types are available for this employee. Assign a leave group with leave types
            and ensure gender on the employee profile matches gender-specific leave rules.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4 px-4 py-3 max-md:grid max-md:grid-cols-2 max-md:gap-3">
          <div>
            <label
              className={`mb-1 block text-xs font-medium ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className={`rounded border px-3 py-2 text-sm max-md:w-full ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))]"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className={`mb-1 block text-xs font-medium ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Leave type
            </label>
            <select
              value={selectedLeaveTypeId}
              onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
              disabled={entitlementLoading || leaveTypeOptions.length === 0}
              className={`min-w-[160px] rounded border px-3 py-2 text-sm max-md:w-full max-md:min-w-0 ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))]"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <option value="">
                {entitlementLoading ? "Loading leave types..." : "Select leave type"}
              </option>
              {leaveTypeOptions.map((lt) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name || lt.leaveTypeName || lt.code || lt.id}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-4 pb-4">
          {loading ? (
            <p
              className={`py-4 text-sm ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Loading...
            </p>
          ) : !selectedLeaveTypeId ? (
            <p
              className={`py-4 text-sm ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Select a leave type to view balance.
            </p>
          ) : monthLabels.length === 0 ? (
            <p
              className={`py-4 text-sm ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              No balance months available for {selectedYear}. Check service commencement, leave type
              effective date, and employee DOJ.
            </p>
          ) : (
            <div className="overflow-x-auto">
              {balanceEntitlement && !balanceEntitlement.hasPolicy && (
                <p
                  className={`mb-3 text-xs ${
                    darkMode ? "text-amber-400/90" : "text-amber-700"
                  }`}
                >
                  No leave policy is configured for this leave type in the employee&apos;s leave
                  group. Credited will stay 0 until it is configured.
                </p>
              )}
              {creditHint && (
                <p
                  className={`mb-3 text-xs ${
                    darkMode ? "text-amber-400/90" : "text-amber-700"
                  }`}
                >
                  {creditHint}
                </p>
              )}
              <table
                className={`w-full border-collapse text-sm max-md:whitespace-nowrap ${
                  darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <thead>
                  <tr
                    className={`border-b ${
                      darkMode
                        ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))]"
                        : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                    }`}
                  >
                    <th
                      className={`px-2 py-2 text-left font-medium max-md:sticky max-md:left-0 max-md:z-[1] ${
                        darkMode ? "max-md:bg-[hsl(var(--ds-bg))]" : "max-md:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      Month
                    </th>
                    <th className="px-2 py-2 text-right font-medium">Opening</th>
                    <th className="px-2 py-2 text-right font-medium">Credited</th>
                    <th className="px-2 py-2 text-right font-medium">Availed</th>
                    <th className="px-2 py-2 text-right font-medium">Adjusted (Dr)</th>
                    <th className="px-2 py-2 text-right font-medium">Adjusted (Cr)</th>
                    <th className="px-2 py-2 text-right font-medium">Encashed</th>
                    <th className="px-2 py-2 text-right font-medium">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {monthLabels.map(({ month, label }) => {
                    const r = getRow(month);
                    return (
                      <tr
                        key={month}
                        className={`border-b ${
                          darkMode
                            ? "border-[hsl(var(--ds-border))]"
                            : "border-[hsl(var(--ds-border))]"
                        }`}
                      >
                        <td className={`px-2 py-2 font-medium ${pinnedMonthCls}`}>{label}</td>
                        <td className="px-2 py-2 text-right">
                          {formatBalanceCell(r.openingBalance, r.ineligible)}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {formatBalanceCell(r.credited, r.ineligible)}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {formatBalanceCell(r.availed, r.ineligible)}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {formatBalanceCell(r.adjustedDebit, r.ineligible)}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {formatBalanceCell(r.adjustedCredit, r.ineligible)}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {formatBalanceCell(r.encashed, r.ineligible)}
                        </td>
                        <td className="px-2 py-2 text-right font-medium">
                          {formatBalanceCell(r.closingBalance, r.ineligible)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
